/**
 * Direct-to-S3 image upload for the admin screens.
 *
 * A request through API Gateway / Lambda can carry only ~6 MB (about 4.5 MB once a binary body is
 * base64-encoded), so an admin image bigger than that can never be POSTed to the API. Above
 * DIRECT_UPLOAD_THRESHOLD_BYTES the screens instead
 *   1. ask the backend for signed S3 URLs (POST /getAdminUploadUrls: names and sizes only),
 *   2. PUT each file straight to S3,
 *   3. call the normal endpoint (uploadNewTheme / updateThemeMedia) with { uploadedFiles }, which
 *      verifies every object in S3 before saving it.
 * Smaller uploads keep using the existing multipart request, unchanged.
 */

export const DIRECT_UPLOAD_THRESHOLD_BYTES = 4 * 1024 * 1024;

/** True when at least one of the files is too big to send through the API. */
export function needsDirectUpload(files) {
  return Object.values(files || {}).some((f) => f && f.size > DIRECT_UPLOAD_THRESHOLD_BYTES);
}

function contentTypeFor(file) {
  const type = String(file.type || '').toLowerCase();
  if (type === 'image/png' || type === 'image/jpeg') return type;
  const ext = String(file.name || '').split('.').pop().toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  return type || 'application/octet-stream';
}

/**
 * Uploads { fieldName: File } straight to S3 and returns { fieldName: filenameInS3 } to send as
 * `uploadedFiles`. Throws an Error whose message can be shown to the admin.
 */
export async function uploadFilesDirect({
  token,
  files,
  backendUrl = process.env.REACT_APP_BACKEND_URL,
  fetchImpl = (...args) => fetch(...args),
}) {
  const entries = Object.entries(files || {}).filter(([, file]) => file);
  if (entries.length === 0) return {};

  const presignRes = await fetchImpl(`${backendUrl}/getAdminUploadUrls`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      files: entries.map(([fieldName, file]) => ({
        fieldName,
        filename: file.name,
        contentType: contentTypeFor(file),
        size: file.size,
      })),
    }),
  });
  const presign = await presignRes.json().catch(() => ({}));
  if (!presignRes.ok || !presign.success) {
    throw new Error(presign.message || `Could not prepare the upload (status ${presignRes.status}).`);
  }

  await Promise.all(
    entries.map(async ([fieldName, file]) => {
      const target = presign.urls && presign.urls[fieldName];
      if (!target || !target.uploadUrl) throw new Error(`No upload address was issued for ${fieldName}.`);
      // Only Content-Type: the signed URL already carries everything else (see themeupdate.jsx).
      const putRes = await fetchImpl(target.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentTypeFor(file) },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Failed to upload ${file.name} (status ${putRes.status}).`);
    })
  );

  return Object.fromEntries(entries.map(([fieldName]) => [fieldName, presign.urls[fieldName].filename]));
}
